import { isCanvas } from '@common/utils';
import { get } from 'lodash';
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import BaseComponent from '../../types/base.type';
import { getValueBinding } from '../shared';
import { IconButton } from 'react-native-paper';
import PointIcon from '../CustomIcon';
import { getActions, getItemListClick, isCheckColor } from '../shared';

type Props = BaseComponent & {
  attributes: {
    icon?: string;
    outlineIcon?: string;
    fontColor: string;
    fontSize: number;
    opacity: number;
    editable: boolean;
  };
  data?: any;
  isInCustomList: boolean;
  record?: any;
  valueInputs?: number | boolean;
};

type BindingValue = {
  currentValue: number;
  maxValue: number;
};
const DEFAULT_CANVAS_VALUE = 3;
const DEFAULT_CANVAS_MAX_VALUE = 5;
const DEFAULT_FONT_COLOR = '#FFD700';

const IconItem = ({
  icon,
  size,
  color,
  onPress,
}: {
  icon: string;
  size: number;
  color: string;
  onPress?: () => void;
}) => {
  if (icon === 'point') {
    return (
      <TouchableOpacity onPress={onPress} style={{ margin: 0 }}>
        <PointIcon width={size} height={size} fill={color} />
      </TouchableOpacity>
    );
  }
  return (
    <IconButton
      icon={icon}
      size={size}
      color={color}
      style={{ margin: 0 }}
      onPress={onPress}
    />
  );
};
const IconList = ({
  icon,
  outlineIcon,
  value,
  maxValue,
  fontColor,
  fontSize,
  onPress,
}: {
  icon: string;
  outlineIcon?: string;
  value: number;
  maxValue: number;
  fontColor: string;
  fontSize: number;
  onPress?: (selectedValue: number) => void;
}) => {
  const filledStars = Math.min(Math.round(value), maxValue);
  return (
    <>
      {Array.from({ length: maxValue }, (_, index) => {
        /* Render filled stars */
        if (index < filledStars) {
          return (
            <IconItem
              key={index}
              icon={icon}
              size={fontSize}
              color={fontColor}
              onPress={() => {
                onPress?.(index + 1);
              }}
            />
          );
        }
        /* Render empty stars */
        return outlineIcon ? (
          <IconItem
            key={index}
            icon={outlineIcon}
            size={fontSize}
            color={fontColor}
            onPress={() => {
              onPress?.(index + 1);
            }}
          />
        ) : null;
      })}
    </>
  );
};
export const StarRate: React.FC<Props> = (props) => {
  const {
    attributes,
    width,
    height,
    data,
    isInCustomList,
    changeInput,
    valueInputs,
    style,
  } = props;
  const {
    icon = 'star',
    outlineIcon,
    fontColor,
    fontSize = 20,
    opacity = 1,
    editable,
  } = attributes;

  // Prioritize style object properties over attributes
  const finalFontSize = style?.fontSize || fontSize;
  const finalFontColor = style?.color || fontColor;
  const finalOpacity = style?.opacity !== undefined ? style.opacity : opacity;
  const canvas = isCanvas();
  const actionId = getActions(props, 'onPress');

  const bindingValue = getValueBinding(
    isInCustomList ? props.initId : props.id,
    isInCustomList ? props?.record : data,
    props
  ) as BindingValue;
  const _currentValue =
    valueInputs !== undefined && typeof valueInputs === 'number'
      ? +valueInputs
      : +get(bindingValue, 'currentValue', 0);
  const _maxValue = +get(bindingValue, 'maxValue', 0);
  const currentValue =
    !Number.isNaN(_currentValue) && _currentValue > 0
      ? _currentValue
      : canvas
      ? DEFAULT_CANVAS_VALUE
      : 0;
  const maxValue =
    !Number.isNaN(_maxValue) && _maxValue > 0
      ? _maxValue
      : canvas
      ? DEFAULT_CANVAS_MAX_VALUE
      : 0;
  const onPress = (selectedValue: number) => {
    if (!editable) {
      return;
    }
    changeInput(selectedValue);
    props.onPress?.(actionId, {
      itemListClick: getItemListClick(props?.record),
    });
  };

  return (
    <View
      style={{
        width,
        height,
        flexDirection: 'row',
        alignItems: 'center',
        opacity: finalOpacity,
      }}
    >
      <IconList
        icon={icon}
        outlineIcon={outlineIcon}
        value={currentValue}
        maxValue={maxValue}
        fontColor={
          isCheckColor(finalFontColor) ? finalFontColor : DEFAULT_FONT_COLOR
        }
        fontSize={finalFontSize}
        onPress={onPress}
      />
    </View>
  );
};
